import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FaFire, FaStar, FaHandSparkles, FaLayerGroup } from 'react-icons/fa';
import { FiShoppingCart, FiX, FiTag } from 'react-icons/fi';
import { IoHeartOutline, IoHeart } from 'react-icons/io5';
import { MdVerified } from 'react-icons/md';
import placeholderA from '../../assets/placeholderA.png';
import ImportModal from './ImportModal';
import PushToStoreModal from '../../pages/dashboard/components/PushToStoreModal';
import WalletHistoryPrice from 'components/Wallet/WalletHistoryPrice';
import { add_to_cart } from 'redux/actions/productActions';

function resolveImg(item) {
    if (!item?.cf_singleimage) return item?.image || placeholderA;
    try {
        const raw = item.cf_singleimage.trim();
        if (raw.startsWith('http')) return raw;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed[0] || placeholderA : placeholderA;
    } catch {
        return item.cf_singleimage || item?.image || placeholderA;
    }
}

const SECTION_CONFIG = {
    'Trending Now': {
        icon: <FaFire size={10} />,
        label: 'Hot',
        accentColor: '#EF4444',
        accentBg: '#FEF2F2',
        headerIcon: <FaFire size={16} color="#EF4444" />,
    },
    'Best Sellers': {
        icon: <FaStar size={10} />,
        label: 'Top',
        accentColor: '#D97706',
        accentBg: '#FFFBEB',
        headerIcon: <FaStar size={16} color="#D97706" />,
    },
    'New Arrivals': {
        icon: <FaHandSparkles size={10} />,
        label: 'New',
        accentColor: '#0B59D5',
        accentBg: '#EFF6FF',
        headerIcon: <FaHandSparkles size={16} color="#0B59D5" />,
    },
};

export function CartToast({ item, image, onClose }) {
    useEffect(() => {
        const t = setTimeout(onClose, 3200);
        return () => clearTimeout(t);
    }, [onClose]);


    return (
        <div style={toastS.wrap}>
            <div style={toastS.bar} />
            <img
                src={image}
                alt=""
                style={toastS.img}
                onError={(e) => {
                    e.target.src = placeholderA;
                }}
            />
            <div style={toastS.text}>
                <p style={toastS.label}>Added to cart ✓</p>
                <p style={toastS.name}>{item?.item_name || item?.group_name || 'Product'}</p>
            </div>
            <button style={toastS.close} onClick={onClose}>
                <FiX size={13} />
            </button>
        </div>
    );
}

function VariantsModal({ item, onClose, onAddToCart }) {
    const variants = item?.variants ?? [];

    return (
        <div style={modalS.overlay} onClick={onClose}>
            <div style={modalS.box} onClick={(e) => e.stopPropagation()}>
                <div style={modalS.header}>
                    <div style={modalS.handle} />
                    <div style={modalS.headerRow}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {item?.brand && <p style={modalS.brand}>{item.brand}</p>}
                            <h3 style={modalS.title}>{item?.group_name || item?.item_name}</h3>
                            <p style={modalS.count}>
                                {variants.length} variant{variants.length !== 1 ? 's' : ''} — tap
                                one to add to cart
                            </p>
                        </div>
                        <button style={modalS.closeBtn} onClick={onClose}>
                            <FiX size={16} />
                        </button>
                    </div>
                </div>

                <div style={modalS.scrollArea}>
                    <div style={modalS.grid}>
                        {variants.map((v) => {
                            const hasDiscount =
                                v?.has_discount === true && Number(v?.cf_discount_percentage) > 0;
                            const discountPct = Number(v?.cf_discount_percentage) || 0;
                            const img = resolveImg(v);
                            const inStock = Number(v?.available_stock) > 0;

                            return (
                                <div
                                    key={v.item_id}
                                    style={{ ...modalS.card, opacity: inStock ? 1 : 0.6 }}>
                                    <div style={modalS.imgWrap}>
                                        <img
                                            src={img}
                                            alt={v.item_name}
                                            style={modalS.img}
                                            onError={(e) => {
                                                e.target.src = placeholderA;
                                            }}
                                        />
                                        {hasDiscount && (
                                            <span style={modalS.discPill}>
                                                -{Math.round(discountPct)}%
                                            </span>
                                        )}
                                        {!inStock && (
                                            <div style={modalS.oosOverlay}>Out of Stock</div>
                                        )}
                                    </div>

                                    <div style={modalS.info}>
                                        <div style={modalS.tagRow}>
                                            {v.color && (
                                                <span style={modalS.colorTag}>{v.color}</span>
                                            )}
                                            {v.size && <span style={modalS.sizeTag}>{v.size}</span>}
                                        </div>
                                        <p style={modalS.variantName}>{v.item_name}</p>
                                        <p style={modalS.sku}>SKU: {v.sku}</p>
                                        <div style={modalS.priceBlock}>
                                            <span style={modalS.price}>
                                                <WalletHistoryPrice price={v.rate || 0} />
                                            </span>
                                            {hasDiscount && v.old_rate && (
                                                <span style={modalS.oldPrice}>
                                                    <WalletHistoryPrice price={v.old_rate} />
                                                </span>
                                            )}
                                        </div>
                                        {hasDiscount && (
                                            <span style={modalS.savePill}>
                                                Save {Math.round(discountPct)}%
                                            </span>
                                        )}
                                        <p
                                            style={{
                                                ...modalS.stockText,
                                                color: inStock ? '#16A34A' : '#EF4444',
                                            }}>
                                            {inStock
                                                ? `✓ ${v.available_stock} in stock`
                                                : '✕ Out of stock'}
                                        </p>
                                        <button
                                            disabled={!inStock}
                                            style={{
                                                ...modalS.addBtn,
                                                background: inStock ? '#1B3B5F' : '#E2E8F0',
                                                color: inStock ? '#fff' : '#94A3B8',
                                                cursor: inStock ? 'pointer' : 'not-allowed',
                                            }}
                                            onClick={() => inStock && onAddToCart(v, img)}>
                                            <FiShoppingCart size={11} style={{ marginRight: 4 }} />
                                            {inStock ? 'Add to Cart' : 'Unavailable'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function MiniCard({ item, title, onAddToCart, onStore, onWishlist }) {
    const navigate = useNavigate();
    const [wishlisted, setWishlisted] = useState(false);
    const [showVariants, setShowVariants] = useState(false);
    const [toast, setToast] = useState(null);
    const config = SECTION_CONFIG[title] ?? SECTION_CONFIG['New Arrivals'];

    const hasDiscount = item?.has_discount === true && Number(item?.cf_discount_percentage) > 0;
    const discountPct = Number(item?.cf_discount_percentage) || 0;
    const isVerified = item?.cf_verified === 'Yes';
    const isGrouped = !!item?.group_id;
    const variantCount = item?.variants?.length ?? 0;
    const img = resolveImg(item);

    const handleNavigate = (e) => {
        e.stopPropagation();
        navigate(`/dashboard/product/details/${item.item_id || item.id}`);
    };

    const handleCartOrVariant = (e) => {
        e.stopPropagation();
        if (isGrouped) {
            setShowVariants(true);
        } else {
            onAddToCart(item);
            setToast({ item, image: img });
        }
    };

    const handleVariantAddToCart = (v, vImg) => {
        onAddToCart(v);
        setToast({ item: v, image: vImg });
        setShowVariants(false);
    };

    return (
        <>
            <div style={cardS.wrap}>
                <div style={cardS.imageBox}>
                    <img
                        src={img}
                        alt={item?.item_name || item?.name || 'Product'}
                        style={cardS.img}
                        onClick={handleNavigate}
                        onError={(e) => {
                            e.target.src = placeholderA;
                        }}
                    />
                    <div style={cardS.gradient} />

                    <span
                        style={{
                            ...cardS.sectionTag,
                            color: config.accentColor,
                            background: config.accentBg,
                        }}>
                        {config.icon}
                        {config.label}
                    </span>

                    <div style={cardS.topRightBadges}>
                        {hasDiscount && (
                            <span style={cardS.discBadge}>-{Math.round(discountPct)}%</span>
                        )}
                        {isGrouped && (
                            <span style={cardS.variantBadge}>
                                <FaLayerGroup size={7} style={{ marginRight: 2 }} />
                                {variantCount}
                            </span>
                        )}
                    </div>

                    {isVerified && (
                        <span style={cardS.verifiedPin} title="Verified">
                            <MdVerified size={11} />
                        </span>
                    )}

                    <div style={cardS.actions}>
                        <button
                            style={cardS.iconBtn}
                            onClick={handleCartOrVariant}
                            title={isGrouped ? 'Choose variant' : 'Add to cart'}>
                            <FiShoppingCart size={12} />
                        </button>
                        <button
                            style={{ ...cardS.iconBtn, color: wishlisted ? '#EF4444' : '#1B3B5F' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setWishlisted((v) => !v);
                                onWishlist?.(item);
                            }}>
                            {wishlisted ? <IoHeart size={12} /> : <IoHeartOutline size={12} />}
                        </button>
                        {onStore && (
                            <button
                                style={cardS.iconBtn}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onStore(item);
                                }}>
                                <span style={{ fontSize: 12, fontWeight: 800, lineHeight: 1 }}>
                                    +
                                </span>
                            </button>
                        )}
                    </div>
                </div>

                <div style={cardS.body} onClick={handleNavigate}>
                    <p style={cardS.name}>
                        {isGrouped
                            ? item.group_name ?? item.item_name ?? item.name ?? 'Product'
                            : item.item_name ?? item.name ?? 'Product'}
                    </p>
                    <div style={cardS.skuRow}>
                        <FiTag size={8} style={{ color: '#94A3B8', flexShrink: 0 }} />
                        <span style={cardS.sku}>
                            {item?.sku || (isGrouped ? `GRP-${item?.group_id?.slice(-6)}` : '—')}
                        </span>
                    </div>
                    <div style={cardS.priceRow}>
                        <span style={{ ...cardS.priceMain, color: config.accentColor }}>
                            <WalletHistoryPrice price={item.rate || item.price || 0} />
                        </span>
                        {hasDiscount && item?.old_rate && (
                            <span style={cardS.oldPrice}>
                                <WalletHistoryPrice price={item.old_rate} />
                            </span>
                        )}
                    </div>
                    <p style={cardS.earn}>
                        Earn up to{' '}
                        <strong>
                            <WalletHistoryPrice
                                price={(3 / 100) * (item.rate || item.price || 0)}
                            />
                        </strong>
                        /Pack
                    </p>
                    {isGrouped ? (
                        <button
                            style={cardS.variantBtn}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowVariants(true);
                            }}>
                            <FaLayerGroup size={9} style={{ marginRight: 4 }} />
                            Choose Variant ({variantCount})
                        </button>
                    ) : (
                        <button style={cardS.cartBtn} onClick={handleCartOrVariant}>
                            <FiShoppingCart size={10} style={{ marginRight: 4 }} />
                            Add to Cart
                        </button>
                    )}
                </div>
            </div>

            {showVariants && (
                <VariantsModal
                    item={item}
                    onClose={() => setShowVariants(false)}
                    onAddToCart={handleVariantAddToCart}
                />
            )}
            {toast && (
                <CartToast item={toast.item} image={toast.image} onClose={() => setToast(null)} />
            )}
        </>
    );
}

const NewArrivalsM = ({ title, products, loading, isMobileDashboard }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const scrollRef = useRef(null);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [isStoreOpen, setIsStoreOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState({});

    const sectionTitle = title || 'New Arrivals';
    const config = SECTION_CONFIG[sectionTitle] ?? SECTION_CONFIG['New Arrivals'];
    const topItems =
        products && products.length > 0 ? (title ? products : products.slice(0, 10)) : [];

    if (!loading && topItems.length === 0) return null;

    const handleAddToCart = (productItem) => {
        dispatch(add_to_cart({ productId: productItem.item_id, qty: 1 }));
    };

    return (
        <section style={secS.wrap}>
            <div style={secS.header}>
                <div style={secS.headerLeft}>
                    {config.headerIcon}
                    <h3 style={secS.heading}>{sectionTitle}</h3>
                </div>
                <button style={secS.viewAll} onClick={() => navigate('/dashboard/products')}>
                    View All →
                </button>
            </div>
            <div ref={scrollRef} style={secS.track}>
                {loading
                    ? Array.from({ length: 4 }).map((_, i) => <div key={i} style={secS.skeleton} />)
                    : topItems.map((item, i) => (
                          <MiniCard
                              key={item.item_id ?? i}
                              item={item}
                              title={sectionTitle}
                              onAddToCart={handleAddToCart}
                              onStore={
                                  isMobileDashboard
                                      ? undefined
                                      : (it) => {
                                            setCurrentItem(it);
                                            setIsStoreOpen(true);
                                        }
                              }
                              onWishlist={(it) => {
                                  setCurrentItem(it);
                                  setIsImportOpen(true);
                              }}
                          />
                      ))}
            </div>
            <ImportModal
                isModalOpen={isImportOpen}
                closeModal={() => setIsImportOpen(false)}
                setIsModalOpen={setIsImportOpen}
                item={currentItem}
            />
            <PushToStoreModal
                isModalOpen={isStoreOpen}
                closeModal={() => setIsStoreOpen(false)}
                setIsModalOpen={setIsStoreOpen}
                item={currentItem}
            />
        </section>
    );
};

export default NewArrivalsM;

const secS = {
    wrap: {
        marginBottom: 32,
        background: '#fff',
        borderRadius: 16,
        padding: '20px 0',
        boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        border: '1px solid #F1F5F9',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 20px 16px',
        borderBottom: '1px solid #F1F5F9',
        marginBottom: 16,
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: 8 },
    heading: {
        margin: 0,
        fontSize: 17,
        fontWeight: 800,
        color: '#0F172A',
        letterSpacing: '-0.02em',
    },
    viewAll: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 700,
        color: '#0B59D5',
        padding: 0,
    },
    track: {
        display: 'flex',
        overflowX: 'auto',
        gap: 12,
        padding: '0 20px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
    },
    skeleton: {
        minWidth: 155,
        height: 255,
        borderRadius: 14,
        background: 'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.4s infinite',
        flexShrink: 0,
    },
};

const cardS = {
    wrap: {
        minWidth: 155,
        maxWidth: 175,
        flexShrink: 0,
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #EEF2F7',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        cursor: 'pointer',
    },
    imageBox: {
        position: 'relative',
        width: '100%',
        paddingTop: '88%',
        background: '#F4F7FB',
        overflow: 'hidden',
    },
    img: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform 0.4s ease',
    },
    gradient: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '35%',
        background: 'linear-gradient(to top,rgba(0,0,0,0.14),transparent)',
        pointerEvents: 'none',
    },
    sectionTag: {
        position: 'absolute',
        top: 6,
        left: 6,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 8,
        fontWeight: 800,
        padding: '2px 6px',
        borderRadius: 20,
        letterSpacing: '0.03em',
    },
    topRightBadges: {
        position: 'absolute',
        top: 6,
        right: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        alignItems: 'flex-end',
    },
    discBadge: {
        background: '#EF4444',
        color: '#fff',
        fontSize: 8,
        fontWeight: 800,
        padding: '2px 5px',
        borderRadius: 10,
    },
    variantBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        background: '#EFF6FF',
        color: '#0B59D5',
        fontSize: 8,
        fontWeight: 800,
        padding: '2px 5px',
        borderRadius: 10,
    },
    verifiedPin: {
        position: 'absolute',
        bottom: 6,
        left: 6,
        background: 'rgba(255,255,255,0.92)',
        color: '#2563EB',
        borderRadius: '50%',
        width: 20,
        height: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
    },
    actions: {
        position: 'absolute',
        bottom: 6,
        right: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    iconBtn: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.93)',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#1B3B5F',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    },
    body: { padding: '8px 9px 10px', display: 'flex', flexDirection: 'column', gap: 3, flex: 1 },
    name: {
        margin: 0,
        fontSize: 11,
        fontWeight: 700,
        color: '#0F172A',
        lineHeight: 1.35,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    skuRow: { display: 'flex', alignItems: 'center', gap: 3 },
    sku: {
        fontSize: 8,
        color: '#94A3B8',
        fontFamily: 'monospace',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    priceRow: { display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 1 },
    priceMain: { fontSize: 12, fontWeight: 800 },
    oldPrice: { fontSize: 10, color: '#94A3B8', textDecoration: 'line-through' },
    earn: {
        margin: 0,
        fontSize: 8,
        color: '#64748B',
        borderTop: '1px solid #F1F5F9',
        paddingTop: 3,
        marginTop: 1,
    },
    cartBtn: {
        marginTop: 5,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 0',
        background: '#1B3B5F',
        color: '#fff',
        border: 'none',
        borderRadius: 7,
        fontSize: 10,
        fontWeight: 700,
        cursor: 'pointer',
    },
    variantBtn: {
        marginTop: 5,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 0',
        background: 'transparent',
        color: '#0B59D5',
        border: '1.5px solid #0B59D5',
        borderRadius: 7,
        fontSize: 10,
        fontWeight: 700,
        cursor: 'pointer',
    },
};

const modalS = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(10,18,38,0.6)',
        zIndex: 5000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(5px)',
        animation: 'fadeIn 0.2s ease',
        padding: '12px',
        overflow: 'hidden',
    },
    box: {
        background: '#fff',
        borderRadius: 20,
        width: '100%',
        maxWidth: 640,
        height: '90vh',
        maxHeight: 680,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        animation: 'scaleIn 0.25s cubic-bezier(0.34,1.56,0.64,1)',
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
    },
    header: {
        flexShrink: 0,
        background: '#fff',
        borderBottom: '1px solid #F1F5F9',
        paddingBottom: 12,
    },
    handle: { width: 36, height: 4, background: '#E2E8F0', borderRadius: 4, margin: '12px auto 0' },
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '10px 16px 0',
        gap: 12,
    },
    brand: {
        margin: 0,
        fontSize: 9,
        fontWeight: 700,
        color: '#94A3B8',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    },
    title: { margin: '2px 0', fontSize: 15, fontWeight: 800, color: '#0F172A', lineHeight: 1.3 },
    count: { margin: 0, fontSize: 10, color: '#64748B' },
    closeBtn: {
        width: 30,
        height: 30,
        borderRadius: '50%',
        background: '#F1F5F9',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#475569',
        flexShrink: 0,
    },
    scrollArea: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        scrollbarColor: '#CBD5E1 transparent',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 2fr))',
        gap: 10,
        padding: '12px 14px 20px',
    },
    card: {
        background: '#FAFBFD',
        borderRadius: 12,
        border: '1px solid #EEF2F7',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    imgWrap: {
        position: 'relative',
        width: '100%',
        paddingTop: '52%',
        background: '#F4F7FB',
        overflow: 'hidden',
        flexShrink: 0,
    },
    img: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
    discPill: {
        position: 'absolute',
        top: 4,
        right: 4,
        background: '#EF4444',
        color: '#fff',
        fontSize: 8,
        fontWeight: 800,
        padding: '2px 5px',
        borderRadius: 10,
    },
    oosOverlay: {
        position: 'absolute',
        inset: 0,
        background: 'rgba(255,255,255,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 700,
        color: '#94A3B8',
        backdropFilter: 'blur(2px)',
    },
    info: { padding: '7px 8px 9px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 },
    tagRow: { display: 'flex', gap: 3, flexWrap: 'wrap' },
    colorTag: {
        fontSize: 9,
        fontWeight: 700,
        padding: '2px 6px',
        background: '#F1F5F9',
        color: '#475569',
        borderRadius: 8,
    },
    sizeTag: {
        fontSize: 9,
        fontWeight: 700,
        padding: '2px 6px',
        background: '#EFF6FF',
        color: '#0B59D5',
        borderRadius: 8,
    },
    variantName: {
        margin: 0,
        fontSize: 10,
        fontWeight: 600,
        color: '#0F172A',
        lineHeight: 1.3,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    sku: {
        margin: 0,
        fontSize: 8,
        color: '#94A3B8',
        fontFamily: 'monospace',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    priceBlock: { display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' },
    price: { fontSize: 12, fontWeight: 800, color: '#0B59D5' },
    oldPrice: {
        fontSize: 10,
        fontWeight: 500,
        color: '#94A3B8',
        textDecoration: 'line-through',
        textDecorationColor: '#EF4444',
        textDecorationThickness: '1px',
    },
    savePill: {
        fontSize: 8,
        fontWeight: 700,
        color: '#DC2626',
        background: '#FEF2F2',
        padding: '1px 5px',
        borderRadius: 8,
    },
    stockText: { margin: 0, fontSize: 9, fontWeight: 600 },
    addBtn: {
        marginTop: 'auto',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '7px 0',
        border: 'none',
        borderRadius: 8,
        fontSize: 10,
        fontWeight: 700,
        transition: 'opacity 0.15s',
    },
};

const toastS = {
    wrap: {
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        background: '#0F172A',
        borderRadius: 12,
        padding: '10px 12px 10px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        animation: 'slideUp 0.3s ease',
        maxWidth: 270,
        overflow: 'hidden',
    },
    bar: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        height: 3,
        width: '100%',
        background: 'linear-gradient(90deg,#0B59D5,#38BDF8)',
        borderRadius: '0 0 12px 12px',
        animation: 'shrink 3.2s linear forwards',
    },
    img: {
        width: 38,
        height: 38,
        borderRadius: 7,
        objectFit: 'cover',
        flexShrink: 0,
        border: '1.5px solid rgba(255,255,255,0.1)',
    },
    text: { flex: 1, minWidth: 0 },
    label: {
        margin: 0,
        fontSize: 9,
        fontWeight: 700,
        color: '#38BDF8',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
    },
    name: {
        margin: 0,
        fontSize: 11,
        fontWeight: 700,
        color: '#fff',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    close: {
        background: 'none',
        border: 'none',
        color: '#64748B',
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        flexShrink: 0,
    },
};
